// eslint-disable-next-line import/no-unresolved
import { toast } from 'react-toastify';

import { Sentry } from '@config';
import { configuredFetchBaseQuery } from '@config/rtkQuery';
import {
  documentTypeMapToColumns,
  documentTypeMapToFields,
} from '@features/documentsService';
import { removeEmpty } from '@features/removeEmpty';
import { cartApi } from '@reducers/order';
import { createSlice } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/dist/query/react';

export const profileApi = createApi({
  reducerPath: 'profileApi',
  baseQuery: configuredFetchBaseQuery(),
  endpoints: (builder) => ({
    getList: builder.query({
      query: (arg) => {
        const url = 'customer/account';
        const { page, size, ...params } = arg;
        return { url, params: { page, page_size: size, ...params } };
      },
      providesTags: ['Profiles'],
    }),
    getForSelect: builder.query({
      query: () => ({ url: 'customer/account/for-select' }),
    }),
    getForSelectWithoutBlocked: builder.query({
      query: () => ({ url: 'customer/account/for-select-without-blocked' }),
    }),
    getDetails: builder.query({
      query: ({ id }) => ({ url: `/customer/account/${id}` }),
      transformResponse: (response) => {
        const { name: profileName, default: isDefault } = response;
        return { profileName, isDefault };
      },
      providesTags: (result, error, { id }) => [
        { tag: 'ProfileData', type: id },
      ],
    }),
    getEndUserData: builder.query({
      query: (arg) => {
        const { id } = arg;
        return { url: `customer/account/${id}/end-user-data` };
      },
      transformResponse: (response) => ({
        companyName: response.company_name,
        firstName: response.first_name,
        lastName: response.last_name,
        streetAddress: response.street,
        numberAddress: response.house_number,
        additionalAddressInfo: response.additional_address_info,
        postalCode: response.postal_code,
        area: response.area,
        country: response.country,
        phoneNumber: response.phone_number,
        emailAddress: response.email,
        vatNumber: response.vat_number,
        companyRegistrationNumber: response.company_registration_number,
      }),
      providesTags: (result, error, { id }) => [
        { tag: 'EndUserData', type: id },
      ],
    }),
    getProfileDocuments: builder.query({
      query: (arg) => {
        const { id } = arg;
        return { url: `customer/account/${id}/documents` };
      },
      transformResponse: (response) => {
        const data = {};
        const { results } = response;
        results.forEach(({ document_type: type, active, validated }) => {
          data[documentTypeMapToColumns[type.id]] = active && validated;
        });
        return data;
      },
      providesTags: (result, error, { id }) => [
        { tag: 'ProfileDocuments', id },
      ],
    }),
    getDocumentsList: builder.query({
      query: (arg) => {
        const { id } = arg;
        return { url: `customer/account/${id}/documents` };
      },
      transformResponse: (response) => {
        const { results } = response;

        return results.map((item) => ({
          documentType: item.document_type,
          validated: item.validated,
          active: item.active,
          submitted: item.date_submitted,
          fileName: item.document_filename,
          fileLocation: item.document_file,
        }));
      },
      providesTags: (result, error, { id }) => [{ tag: 'Documents', id }],
    }),
    checkUniqueName: builder.query({
      query: ({ name }) => ({
        url: `customer/account/check-unique`,
        params: { name },
      }),
    }),
    createProfile: builder.mutation({
      query(arg) {
        const { data } = arg;
        return {
          url: `customer/account`,
          method: 'POST',
          body: data,
        };
      },
      invalidatesTags: (result, error) => (error ? [] : ['Profiles']),
    }),
    createEndUserData: builder.mutation({
      query({ id, data }) {
        return {
          url: `customer/account/${id}/end-user-data`,
          method: 'POST',
          body: data,
        };
      },
      transformResponse: (response) => response.message,
      transformErrorResponse: (response) => {
        if (!response.data) {
          return response;
        }
        const { reg_type: contactType, email, level } = response.data;
        const errors = removeEmpty({ level, email, contactType });
        Object.entries(errors).forEach(([key, value]) => {
          errors[key] = value.join(' ');
        });
        return errors;
      },
      invalidatesTags: (result, error, { id }) =>
        error ? [] : [{ tag: 'EndUserData', id }],
    }),
    updateEndUserData: builder.mutation({
      query({ id, data }) {
        return {
          url: `customer/account/${id}/end-user-data/update`,
          method: 'PUT',
          body: data,
        };
      },
      transformResponse: (response) => response.message,
      transformErrorResponse: (response) => {
        if (!response.data) {
          return response;
        }
        const { reg_type: contactType, email, level } = response.data;
        const errors = removeEmpty({ level, email, contactType });
        Object.entries(errors).forEach(([key, value]) => {
          errors[key] = value.join(' ');
        });
        return errors;
      },
      invalidatesTags: (result, error, { id }) =>
        error
          ? []
          : [
              { tag: 'EndUserData', type: id },
              { tag: 'ProfileFormData', type: id },
            ],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(cartApi.util.invalidateTags(['Incomplete Orders']));
        } catch (err) {
          /* empty */
        }
      },
    }),
    uploadDocuments: builder.mutation({
      query(arg) {
        const { id, data } = arg;
        const bodyFormData = new FormData();
        if (Object.keys(data).length !== 0) {
          Object.entries(data).forEach(([key, value]) => {
            if (key === 'country' || key === 'area') {
              bodyFormData.append(key, value);
              return;
            }
            for (let i = 0; i < value.length; i += 1) {
              bodyFormData.append(key, value[i]);
            }
          });
        }

        return {
          url: `customer/account/${id}/documents`,
          method: 'POST',
          body: bodyFormData,
          formData: true,
        };
      },
      invalidatesTags: (result, error, { id }) =>
        error
          ? []
          : [
              { tag: 'ProfileDocuments', id },
              { tag: 'Documents', id },
            ],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(cartApi.util.invalidateTags(['Incomplete Orders']));
        } catch (err) {
          /* empty */
        }
      },
    }),
    getPlanProfileRequirements: builder.query({
      query: (arg) => {
        const { id, type } = arg;
        return { url: `/customer/plans/${id}/type/${type}/documents-needed` };
      },
      transformResponse: (response) => {
        const data = {};
        response.forEach(({ document_type: type }) => {
          if (!(type.id in documentTypeMapToFields)) {
            Sentry.captureException(
              `Missing configuration for document ID - ${type.id}`
            );
            return;
          }
          const { fields, documents } = documentTypeMapToFields[type.id];
          if (fields && fields.length > 0) {
            fields.forEach((item) => {
              data[item] = true;
            });
          }
          if (documents && documents.length > 0) {
            documents.forEach((item) => {
              data[item] = true;
            });
          }
        });
        return data;
      },
    }),
    getProfileFormData: builder.query({
      query: ({ id, planID, planType, did }) => {
        const baseURL = `customer/account/${id}/form`;
        if (did) {
          return { url: `${baseURL}/did/${did}` };
        }
        if (planID && planType) {
          return { url: `${baseURL}/plan/${planID}/type/${planType}` };
        }
        return { url: baseURL };
      },
      providesTags: (result, error, { id }) => [
        { tag: 'ProfileFormData', type: id },
      ],
    }),
    updateProfile: builder.mutation({
      query: ({ id, data }) => ({
        url: `customer/account/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) =>
        error ? [] : [{ tag: 'ProfileData', type: id }, 'Profiles'],
    }),
  }),
});

const initialState = {
  profiles: { isLoading: false, data: [] },
  createProfile: {
    isLoading: false,
    unique: false,
    name: null,
  },
  isNameUnique: false,
  profile: {
    isLoading: false,
    data: {},
    requirements: { data: {}, isLoading: false },
  },
  documents: { isLoading: false, data: [] },
  toast: {},
};

const profileSlice = createSlice({
  name: 'profile',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addMatcher(
        profileApi.endpoints.createEndUserData.matchPending,
        (state, action) => {
          state.toast[action.meta.requestId] =
            toast.loading('Saving profile...');
        }
      )
      .addMatcher(
        profileApi.endpoints.createEndUserData.matchFulfilled,
        (state, action) => {
          toast.update(state.toast[action.meta.requestId], {
            render: 'Profile is saved.',
            type: 'success',
            isLoading: false,
            autoClose: 5000,
          });
        }
      )
      .addMatcher(
        profileApi.endpoints.createEndUserData.matchRejected,
        (state, action) => {
          toast.update(state.toast[action.meta.requestId], {
            render: 'Error occurred.',
            type: 'error',
            isLoading: false,
            autoClose: 5000,
          });
        }
      )
      .addMatcher(
        profileApi.endpoints.updateEndUserData.matchPending,
        (state, action) => {
          state.toast[action.meta.requestId] =
            toast.loading('Saving profile...');
        }
      )
      .addMatcher(
        profileApi.endpoints.updateEndUserData.matchFulfilled,
        (state, action) => {
          toast.update(state.toast[action.meta.requestId], {
            render: 'Profile is saved.',
            type: 'success',
            isLoading: false,
            autoClose: 5000,
          });
        }
      )
      .addMatcher(
        profileApi.endpoints.updateEndUserData.matchRejected,
        (state, action) => {
          toast.update(state.toast[action.meta.requestId], {
            render: 'Error occurred.',
            type: 'error',
            isLoading: false,
            autoClose: 5000,
          });
        }
      )
      .addMatcher(
        profileApi.endpoints.uploadDocuments.matchPending,
        (state, action) => {
          state.toast[action.meta.requestId] = toast.loading(
            'Saving documents...'
          );
        }
      )
      .addMatcher(
        profileApi.endpoints.uploadDocuments.matchFulfilled,
        (state, action) => {
          toast.update(state.toast[action.meta.requestId], {
            render: 'Documents saved.',
            type: 'success',
            isLoading: false,
            autoClose: 5000,
          });
        }
      )
      .addMatcher(
        profileApi.endpoints.uploadDocuments.matchRejected,
        (state, action) => {
          toast.update(state.toast[action.meta.requestId], {
            render: 'Error occurred.',
            type: 'error',
            isLoading: false,
            autoClose: 5000,
          });
        }
      )
      .addMatcher(
        profileApi.endpoints.checkUniqueName.matchFulfilled,
        (state, action) => {
          state.isNameUnique = action.payload.unique;
        }
      );
  },
});

export default profileSlice.reducer;
